import type {
  BeforeSendFn,
  PostHog,
  PostHogConfig,
  PostHogInterface,
} from "posthog-js"
import {
  isFramelessGmeWalletProviderError,
  isFramelessResOperationNoise,
  isLocalDevExceptionHost,
  isExpectedStreamInterruption,
  isReactHydrationMismatchFromGeneratedChunk,
  isSuppressedStorageAccessSecurityError,
  isTrackdeskParamsNotSetException,
} from "./shared/lib/monitoring/client-exception-filters"
import { getBrowserBotSignalProperties } from "./shared/lib/analytics/bot-signals"
import { minimizeAnalyticsProperties, minimizeExceptionList, minimizeFlagEvaluationProperties, minimizePath, minimizeUrl, withRawUserAgent } from "./shared/lib/analytics/privacy"
import {
  getSupplementalMarketingAttribution,
  mergeFirstTouchMarketingAttribution,
} from "./shared/lib/marketing-attribution"
import { isPostHogTelemetryEnabled } from "./shared/lib/monitoring/telemetry-config"

type DeferredPostHogMethod =
  | "capture"
  | "captureException"
  | "identify"
  | "register"
  | "reset"
  | "unregister"

type DeferredPostHogCall = {
  method: DeferredPostHogMethod
  args: unknown[]
}

type PendingFeatureFlagListener = {
  callback: () => void
  cancelled: boolean
  unsubscribe?: () => void
}

interface DeferredPostHogClient {
  capture: (event: string, properties?: Record<string, unknown>) => void
  captureException: (error: unknown, properties?: Record<string, unknown>) => void
  identify: (distinctId: string, properties?: Record<string, unknown>) => void
  register: (properties: Record<string, unknown>, days?: number) => void
  reset: (resetDeviceId?: boolean) => void
  unregister: (property: string) => void
  getFeatureFlag: (key: string) => string | boolean | undefined
  get_distinct_id: () => string
  onFeatureFlags: (callback: () => void) => () => void
}

declare global {
  interface Window {
    __PH_INITED?: boolean
    posthog?: PostHog | DeferredPostHogClient
  }
}

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY
// Local `next dev` pulls the PostHog key from Doppler, so without a gate a
// developer's dev session (Turbopack/dev-overlay errors, HMR noise) would
// autocapture unhandled errors into the shared production project. Only real
// deployed environments capture exceptions — `NODE_ENV === "production"` is
// true for built preview and production, false for `next dev`.
const CAPTURE_EXCEPTIONS = process.env.NODE_ENV === "production"
// Browser ingest uses the managed proxy. ui_host stays on us.posthog.com.
// A telemetry-off build still skips init. Removing the /scpage rewrite does
// not stop tabs that already loaded this host.
const POSTHOG_API_HOST = "https://p.pleasur.ai"
const MARKETING_ATTRIBUTION_COOKIE = "marketing_attribution"
const LEGACY_TRAFFICSTARS_ATTRIBUTION_COOKIE = "trafficstars_attribution"
const MARKETING_ATTRIBUTION_MAX_AGE_SECONDS = 60 * 60 * 24 * 30
const SESSION_RECORDING_START_DELAY_MS = 10_000
const MAX_DEFERRED_POSTHOG_CALLS = 100
const deferredPostHogCalls: DeferredPostHogCall[] = []
const pendingFeatureFlagListeners = new Set<PendingFeatureFlagListener>()
let deferredPostHogTarget: PostHog | null = null
let postHogInitializationPromise: Promise<void> | null = null
const MARKETING_ATTRIBUTION_PARAM_ALIASES: Record<string, string> = {
  click_id: "ts_click_id",
  clickid: "ts_click_id",
  ts_click_id: "ts_click_id",
  ts_session_id: "ts_session_id",
  session_id: "ts_session_id",
  creative: "ts_creative",
  creative_id: "ts_creative",
  ts_creative: "ts_creative",
  site: "ts_site",
  site_id: "ts_site",
  ts_site: "ts_site",
  zone: "ts_zone",
  zone_id: "ts_zone",
  ts_zone: "ts_zone",
  adspot: "ts_adspot",
  adspot_id: "ts_adspot",
  ts_adspot: "ts_adspot",
  device: "ts_device",
  ts_device: "ts_device",
  os: "ts_os",
  ts_os: "ts_os",
  browser: "ts_browser",
  ts_browser: "ts_browser",
  carrier: "ts_carrier",
  ts_carrier: "ts_carrier",
  geo: "ts_geo",
  country: "ts_geo",
  ts_geo: "ts_geo",
  lang: "ts_lang",
  language: "ts_lang",
  ts_lang: "ts_lang",
  pricing: "ts_pricing",
  ts_pricing: "ts_pricing",
  cost: "ts_cost",
  ts_cost: "ts_cost",
  campaign: "utm_campaign",
  campaign_id: "utm_campaign",
  utm_campaign: "utm_campaign",
  utm_medium: "utm_medium",
  utm_source: "utm_source",
  utm_content: "utm_content",
  utm_term: "utm_term",
  utm_id: "utm_id",
  experiment_id: "experiment_id",
  angle: "angle",
}
const FIRST_TOUCH_ATTRIBUTION_KEYS = [
  "initial_referrer",
  "initial_landing_path",
] as const
const MARKETING_ATTRIBUTION_KEYS = Array.from(
  new Set([
    ...Object.values(MARKETING_ATTRIBUTION_PARAM_ALIASES),
    ...FIRST_TOUCH_ATTRIBUTION_KEYS,
  ])
)

type UnknownRecord = Record<string, unknown>
type AttributionPostHogClient = {
  register: (properties: Record<string, unknown>, days?: number) => void
  unregister: (property: string) => void
}
type SessionRecordingConfig = NonNullable<
  Partial<PostHogConfig>["session_recording"]
>

interface ExceptionListItem extends UnknownRecord {
  type?: unknown
  value?: unknown
  mechanism?: unknown
  stacktrace?: ExceptionStacktrace
}

interface ExceptionStacktrace extends UnknownRecord {
  raw?: unknown
  frames?: ExceptionFrame[]
}

interface ExceptionFrame extends UnknownRecord {
  filename?: unknown
  function?: unknown
  context_line?: unknown
  chunk_id?: unknown
}

function browserUserAgent(): string | undefined {
  if (typeof navigator === "undefined") return undefined
  const ua = navigator.userAgent
  return typeof ua === "string" && ua.length > 0 ? ua : undefined
}

function eventProperties(properties: Record<string, unknown>): Record<string, unknown> {
  return minimizeAnalyticsProperties(withRawUserAgent(properties, browserUserAgent()), true)
}

const beforeSend: BeforeSendFn = (event) => {
  if (!event) return event
  // rrweb masking must happen before replay serialization, not in this hook.
  if (event.event === "$snapshot") return event
  // PLE-1968: lets analytics filter automated browsers that send normal-looking pageviews.
  const botSignals = event.event === "$exception" ? null : getBrowserBotSignalProperties()
  const minimized = {
    ...event,
    properties: eventProperties({ ...toRecord(event.properties), ...botSignals }),
    ...(event.$set && { $set: minimizeAnalyticsProperties(toRecord(event.$set)) }),
    ...(event.$set_once && { $set_once: minimizeAnalyticsProperties(toRecord(event.$set_once)) }),
  }
  if (event.event !== "$exception") {
    const freshAttribution = getMarketingAttribution()
    const persistedAttribution = getPersistedMarketingAttribution()
    const attribution = freshAttribution
      ? mergeFirstTouchMarketingAttribution(
          freshAttribution,
          persistedAttribution
        )
      : persistedAttribution
    if (!attribution) return minimized
    if (freshAttribution && !isAttributionAlreadyPersisted(attribution)) {
      persistMarketingAttribution(attribution)
    }
    return {
      ...minimized,
      properties: eventProperties({
        ...getSupplementalMarketingAttribution(attribution),
        ...toRecord(minimized.properties),
      }),
    }
  }

  const props = toRecord(event.properties)
  const exceptionList = getExceptionList(props.$exception_list)
  const firstException = exceptionList[0] ?? null
  const firstValue = getFirstString(props.$exception_values)
  const listValue = valueToString(firstException?.value)
  const msg = valueToString(props.$exception_message) || firstValue || listValue
  const firstType = getFirstString(props.$exception_types)
  const listType = valueToString(firstException?.type)
  const type =
    firstType || listType || valueToString(props.$exception_type)
  const frames = getFrames(firstException)

  if (
    shouldSuppressException({
      props,
      msg,
      type,
      frames,
      mechanism: firstException?.mechanism,
    })
  ) {
    return null
  }

  return {
    ...minimized,
    properties: {
      ...eventProperties(buildExceptionProperties(props)),
      $exception_list: minimizeExceptionList(props.$exception_list),
    },
  }
}

const sessionRecordingConfig: SessionRecordingConfig = {
  maskAllInputs: true,
  maskTextSelector: "*",
  maskAllElementAttributes: true,
  blockSelector: "img,picture,video,audio,canvas,svg,iframe,object,embed,[data-ph-private]",
  captureCanvas: { recordCanvas: false },
  recordCrossOriginIframes: false,
  recordHeaders: false,
  recordBody: false,
  // The hook also handles replay navigation; never copy network payloads.
  maskCapturedNetworkRequestFn: (request) => ({
    name: minimizeUrl(request.name),
    entryType: "resource",
    startTime: typeof request.startTime === "number" ? request.startTime : 0,
    duration: typeof request.duration === "number" ? request.duration : 0,
  }),
}

export function shouldSuppressException({
  props,
  msg,
  type,
  frames,
  mechanism,
}: {
  props: UnknownRecord
  msg: string
  type: string
  frames: ExceptionFrame[]
  mechanism?: unknown
}): boolean {
  if (isLocalDevException()) return true
  if (isTrackdeskParamsNotSetException({ msg, type })) return true
  if (
    type === "AbortError" ||
    msg.includes("AbortError") ||
    msg.includes("signal is aborted") ||
    msg.includes("operation was aborted")
  ) {
    return true
  }
  if (msg.includes("feature named") && msg.includes("was not found")) return true
  if (
    type === "TypeError" &&
    msg.includes("Cannot read properties of undefined") &&
    msg.includes("apply")
  ) {
    return true
  }
  if (msg === "NEXT_REDIRECT" || msg.startsWith("NEXT_REDIRECT")) return true
  if (isSuppressedStorageAccessSecurityError({ type, msg })) {
    return true
  }
  if (allSourcesArePostHogSdk(props.$exception_sources)) return true
  if (type === "UnrecognizedActionError") return true
  if (type === "ChatError") return true
  if (type === "SupabaseHttp403" && isStaleJwtMessage(msg)) return true
  if (msg.trim() === "No suitable key or wrong key type") return true
  if (
    type === "TypeError" &&
    (msg === "Failed to fetch" ||
      msg === "Load failed" ||
      msg === "network error" ||
      msg === "Content-Length header of network response exceeds response Body." ||
      msg === "TypeError: Content-Length header of network response exceeds response Body.")
  ) {
    return true
  }
  if (
    msg === "NetworkError when attempting to fetch resource." ||
    msg === "TypeError: Failed to fetch" ||
    msg === "TypeError: Load failed"
  ) {
    return true
  }
  if (isExpectedStreamInterruption({ msg, type, scope: valueToString(props.scope) })) return true
  // Transient socket/stream drop when a user navigates away or loses signal
  // mid-request. Reported handled (mechanism.handled: true), benign network
  // noise — not a crash or broken flow.
  if (msg === "Connection closed." || msg === "Error: Connection closed.") {
    return true
  }
  // Voice-call teardown race: @vapi-ai/web bundles Daily.co, which runs the call
  // in a hidden iframe. When that iframe is detached during teardown the SDK still
  // reads `iframe.contentWindow.document` while `contentWindow` is null, throwing a
  // TypeError. Engine wording differs — Firefox: `can't access property "document",
  // a.contentWindow is null`; WebKit: `null is not an object (evaluating
  // 'a.contentWindow.document')` — both contain "contentWindow". No app code touches
  // contentWindow, so this can only be the SDK iframe. Benign; call flow is unaffected.
  if (type === "TypeError" && msg.includes("contentWindow")) return true
  if (type === "DOMException" && msg.includes("NotAllowedError")) return true
  if (
    (type === "NotFoundError" || type === "DOMException") &&
    (msg.includes("insertBefore") ||
      msg.includes("removeChild") ||
      msg.includes("The object can not be found here"))
  ) {
    return true
  }
  if (msg.includes("__firefox__")) return true
  // `chrome.runtime.sendMessage` is the browser-extension messaging API — these
  // throw inside users' installed extensions (e.g. "Tab not found.", "An unknown
  // error occurred."), never from our code. window.onerror sweeps them up as
  // inbox noise. No `runtime.sendMessage` usage exists in src/.
  if (msg.includes("runtime.sendMessage")) return true
  if (isFramelessGmeWalletProviderError({ msg, frames, mechanism })) return true
  if (
    frames.length === 1 &&
    valueToString(frames[0]?.function) === "HTMLDocument.c"
  ) {
    return true
  }
  if (
    type === "RangeError" &&
    msg.includes("Maximum call stack size exceeded") &&
    (frames.length === 0 || framesIncludePostHogSessionRecorder(frames))
  ) {
    return true
  }
  if (msg.includes("unexpected response was received from the server")) {
    return true
  }
  if (msg.includes("Failed to find Server Action")) return true
  if (msg === "Script error.") return true
  if (isFramelessResOperationNoise({ msg, frames })) return true
  if (framesAreEntirelyAnonymous(frames)) return true
  if (framesAreEntirelyInjectedScript(frames)) return true
  // React #418 is a hydration mismatch. The current production cluster appears
  // as one obfuscated generated chunk source with no function frames and did not
  // reproduce in clean desktop/mobile checks, which matches external DOM
  // mutation noise. Keep app-route and named framework chunks visible.
  if (
    isReactHydrationMismatchFromGeneratedChunk({
      msg,
      sources: props.$exception_sources,
      functions: props.$exception_functions,
    })
  ) {
    return true
  }

  return !msg && !type
}

// A recursive "Maximum call stack size exceeded" thrown inside posthog-js's
// own session recorder (lazy-loaded-session-recorder `_captureSnapshot` ->
// `capture` -> bot fingerprinting) surfaces with a full SDK frame stack, so the
// `frames.length === 0` gate above misses it. These frames are mixed — our
// webpack bundle plus the SDK file — so `allSourcesArePostHogSdk` misses it too.
// Match the SDK recorder frames directly to keep this SDK-internal recursion out
// of the error inbox without hiding genuine app-level stack overflows.
function framesIncludePostHogSessionRecorder(frames: ExceptionFrame[]): boolean {
  return frames.some((frame) => {
    const filename = valueToString(frame.filename).toLowerCase()
    const fn = valueToString(frame.function)
    return (
      filename.includes("lazy-loaded-session-recorder") ||
      fn === "_captureSnapshot"
    )
  })
}

// Errors from injected third-party scripts (in-app browsers, extensions,
// media-control shims like Amazon's `a2z` namespace) surface with every stack
// frame as `<anonymous>` and no resolvable source — none of them come from our
// webpack bundle, whose frames carry a real filename or chunk_id. Suppress them
// so they don't land in the error inbox as noise. See issue PLE noise triage.
function framesAreEntirelyAnonymous(frames: ExceptionFrame[]): boolean {
  if (frames.length === 0) return false
  return frames.every((frame) => {
    if (frame.chunk_id) return false
    const filename = valueToString(frame.filename).trim()
    return filename === "" || filename === "<anonymous>"
  })
}

// Content-injection scripts (ISP/proxy HTML injection, browser extensions,
// in-app browser shims) surface with a *real* filename and function name — e.g.
// `initQuilter` at `https://pleasur.ai/jsQuilting/server/jsDict_utf8.js` throwing
// `ReferenceError: Can't find variable: ntsp_user_level`. Because the frame carries
// a filename it gets `in_app: true` and slips past `framesAreEntirelyAnonymous`,
// landing in the error inbox as noise. None of these frames come from our bundle:
// our real code is served under `/_next/` or carries a `chunk_id` (both webpack and
// the SDK-matched frames do). The injected file lives on our origin but under a
// foreign path and never matches a chunk, so suppress a stack whose every frame is a
// real-filename, non-`/_next/`, chunk-less script.
function framesAreEntirelyInjectedScript(frames: ExceptionFrame[]): boolean {
  if (frames.length === 0) return false
  return frames.every((frame) => {
    if (frame.chunk_id) return false
    const filename = valueToString(frame.filename).trim()
    if (filename === "" || filename === "<anonymous>") return false
    return !filename.includes("/_next/")
  })
}

function buildExceptionProperties(props: UnknownRecord): UnknownRecord {
  const taggedProps: UnknownRecord = {
    ...props,
    $exception_source: props.$exception_source ?? "client",
    scope:
      props.scope ??
      (getExceptionType(props) === "UnhandledRejection"
        ? "client-unhandled-rejection"
        : "client-unhandled"),
  }

  const routePath = getRoutePath()
  const routeProps = routePath
    ? { ...taggedProps, route_path: taggedProps.route_path ?? routePath }
    : taggedProps

  return scrubExceptionProperties(routeProps)
}

function getExceptionType(props: UnknownRecord): string {
  const firstType = getFirstString(props.$exception_types)
  if (firstType) return firstType
  const firstException = getExceptionList(props.$exception_list)[0]
  const listType = valueToString(firstException?.type)
  if (listType) return listType
  return valueToString(props.$exception_type)
}

function scrubExceptionProperties(props: UnknownRecord): UnknownRecord {
  return {
    ...props,
    $exception_message: scrubIfString(props.$exception_message),
    $exception_stack_trace_raw: scrubIfString(
      props.$exception_stack_trace_raw
    ),
    $exception_list: Array.isArray(props.$exception_list)
      ? props.$exception_list.map(scrubExceptionListItem)
      : props.$exception_list,
  }
}

function scrubExceptionListItem(item: unknown): unknown {
  if (!isRecord(item)) return item
  const stacktrace = isRecord(item.stacktrace)
    ? scrubStacktrace(item.stacktrace)
    : item.stacktrace

  return {
    ...item,
    value: scrubIfString(item.value),
    stacktrace,
  }
}

function scrubStacktrace(stacktrace: UnknownRecord): UnknownRecord {
  return {
    ...stacktrace,
    raw: scrubIfString(stacktrace.raw),
    frames: Array.isArray(stacktrace.frames)
      ? stacktrace.frames.map(scrubFrame)
      : stacktrace.frames,
  }
}

function scrubFrame(frame: unknown): unknown {
  if (!isRecord(frame)) return frame
  return {
    ...frame,
    filename: scrubIfString(frame.filename),
    function: scrubIfString(frame.function),
    context_line: scrubIfString(frame.context_line),
  }
}

function scrubIfString(value: unknown): unknown {
  return typeof value === "string" ? scrub(value) : value
}

function scrub(value: string): string {
  return value
    .replace(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g, "[REDACTED]")
    .replace(
      /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g,
      "[REDACTED]"
    )
    .replace(/[Bb]earer\s+[A-Za-z0-9._-]{20,}/g, "[REDACTED]")
    .replace(/sk[-_][A-Za-z0-9]{20,}/g, "[REDACTED]")
    .replace(/phx_[A-Za-z0-9]{20,}/g, "[REDACTED]")
}

function isStaleJwtMessage(msg: string): boolean {
  const lower = msg.toLowerCase()
  return (
    lower.includes("unrecognized jwt kid") ||
    (lower.includes("invalid jwt") &&
      lower.includes("token is unverifiable"))
  )
}

function allSourcesArePostHogSdk(value: unknown): boolean {
  if (!Array.isArray(value) || value.length === 0) return false
  return value.every((source) => {
    const text = String(source)
    try {
      const url = new URL(text, "https://analytics.invalid")
      return (
        url.hostname === "p.pleasur.ai" || url.pathname.startsWith("/scpage/")
      )
    } catch {
      return false
    }
  })
}

function getExceptionList(value: unknown): ExceptionListItem[] {
  if (!Array.isArray(value)) return []
  return value.filter(isExceptionListItem)
}

function getFrames(item: ExceptionListItem | null): ExceptionFrame[] {
  const frames = item?.stacktrace?.frames
  return Array.isArray(frames) ? frames.filter(isRecord) : []
}

function getFirstString(value: unknown): string {
  if (!Array.isArray(value) || value.length === 0) return ""
  return valueToString(value[0])
}

function valueToString(value: unknown): string {
  if (value === null || value === undefined) return ""
  return String(value)
}

function toRecord(value: unknown): UnknownRecord {
  return isRecord(value) ? value : {}
}

function isExceptionListItem(value: unknown): value is ExceptionListItem {
  return isRecord(value)
}

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value && typeof value === "object" && !Array.isArray(value))
}

// `NODE_ENV === "production"` already disables capture_exceptions for `next
// dev`. A local `next start` / production build on loopback still captures,
// so also drop $exception events from localhost/loopback/mDNS hosts.
// Deployed production and Coolify previews serve on real hostnames.
function isLocalDevException(): boolean {
  if (process.env.NODE_ENV !== "production") return true
  if (typeof window === "undefined") return false
  try {
    return isLocalDevExceptionHost(window.location.hostname)
  } catch {
    return false
  }
}

function getRoutePath(): string | undefined {
  if (typeof window === "undefined") return undefined
  try {
    return window.location.pathname
  } catch {
    return undefined
  }
}

function getMarketingAttribution(): Record<string, string> | null {
  if (typeof window === "undefined") return null
  try {
    const params = new URLSearchParams(window.location.search)
    const attribution: Record<string, string> = {}

    params.forEach((rawValue, rawKey) => {
      const normalizedRawKey = rawKey.toLowerCase()
      const key = MARKETING_ATTRIBUTION_PARAM_ALIASES[normalizedRawKey]
      if (!key) return
      const value = sanitizeAttributionValue(rawValue)
      if (isStripeCheckoutSessionId(normalizedRawKey, value)) return
      if (value) attribution[key] = value
    })

    const referrer = sanitizeAttributionValue(getReferrerOrigin())
    const landingPath = minimizePath(window.location.pathname)
    const firstTouchContext = {
      ...(referrer ? { initial_referrer: referrer } : {}),
      ...(landingPath ? { initial_landing_path: landingPath } : {}),
    }
    // Snapshot untagged visits too, so a later campaign/internal link cannot
    // become the first touch. Native PostHog decides the channel.
    return {
      ...attribution,
      ...firstTouchContext,
    }
  } catch {
    return null
  }
}

function getReferrerOrigin(): string {
  if (!document.referrer) return ""
  try {
    const referrer = new URL(document.referrer)
    return referrer.origin
  } catch {
    return ""
  }
}

function getPersistedMarketingAttribution(): Record<string, string> | null {
  if (typeof document === "undefined") return null
  const raw =
    getCookieValue(MARKETING_ATTRIBUTION_COOKIE) ??
    getCookieValue(LEGACY_TRAFFICSTARS_ATTRIBUTION_COOKIE)
  if (!raw) return null

  const parsed = parseAttributionCookie(raw)
  if (!isRecord(parsed)) return null

  const attribution = MARKETING_ATTRIBUTION_KEYS.reduce<Record<string, string>>(
    (attrs, key) => {
      const value = parsed[key]
      if (typeof value === "string" && value.trim()) {
        attrs[key] = sanitizeAttributionValue(value)
      }
      return attrs
    },
    {}
  )

  if (!Object.keys(attribution).length) return null

  return attribution
}

function getCookieValue(name: string): string | null {
  try {
    const prefix = `${name}=`
    const cookie = document.cookie
      .split(";")
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(prefix))
    return cookie?.slice(prefix.length) ?? null
  } catch {
    // Blocked browser storage must not stop native analytics or authentication.
    return null
  }
}

function parseAttributionCookie(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    try {
      const decoded = decodeURIComponent(raw)
      return decoded === raw ? null : JSON.parse(decoded)
    } catch {
      return null
    }
  }
}

function sanitizeAttributionValue(value: string): string {
  return value.trim().slice(0, 256)
}

function isStripeCheckoutSessionId(key: string, value: string): boolean {
  return key === "session_id" && /^cs_(live|test)_/.test(value)
}

function isAttributionAlreadyPersisted(
  attribution: Record<string, string>
): boolean {
  const raw = getCookieValue(MARKETING_ATTRIBUTION_COOKIE)
  if (!raw) return false

  try {
    return raw === encodeURIComponent(JSON.stringify(attribution))
  } catch {
    return false
  }
}

function persistMarketingAttribution(
  attribution: Record<string, string>
): void {
  try {
    const encoded = encodeURIComponent(JSON.stringify(attribution))
    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie =
      `${MARKETING_ATTRIBUTION_COOKIE}=${encoded}; Max-Age=${MARKETING_ATTRIBUTION_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`
  } catch {
    // Attribution persistence must not block PostHog initialization.
  }
}

function applyMarketingAttribution(client: AttributionPostHogClient): void {
  const freshMarketingAttribution = getMarketingAttribution()
  const persistedMarketingAttribution = getPersistedMarketingAttribution()
  const marketingAttribution =
    freshMarketingAttribution
      ? mergeFirstTouchMarketingAttribution(
          freshMarketingAttribution,
          persistedMarketingAttribution
        )
      : persistedMarketingAttribution
  // Clear the older implementation's persistent UTM overrides before the SDK's
  // first pageview. This does not touch its native session persistence.
  unregisterMarketingAttribution(client)
  if (marketingAttribution) {
    client.register(getSupplementalMarketingAttribution(marketingAttribution))
    if (!isAttributionAlreadyPersisted(marketingAttribution)) {
      persistMarketingAttribution(marketingAttribution)
    }
  }
}

function startSessionRecordingAfterCriticalPaint(
  client: PostHogInterface,
): void {
  window.setTimeout(() => {
    const start = () => client.startSessionRecording()
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(start, { timeout: 2_000 })
      return
    }
    start()
  }, SESSION_RECORDING_START_DELAY_MS)
}

function handlePostHogLoaded(client: PostHogInterface): void {
  minimizeFlagEvaluationProperties(client)
  applyMarketingAttribution(client)
  startSessionRecordingAfterCriticalPaint(client)
}

function enqueuePostHogCall(
  method: DeferredPostHogMethod,
  args: unknown[]
): void {
  if (deferredPostHogTarget) {
    invokePostHogCall(deferredPostHogTarget, { method, args })
    return
  }

  if (deferredPostHogCalls.length >= MAX_DEFERRED_POSTHOG_CALLS) {
    deferredPostHogCalls.shift()
  }
  deferredPostHogCalls.push({ method, args })
}

function invokePostHogCall(client: PostHog, call: DeferredPostHogCall): void {
  try {
    const method = (client as unknown as Record<string, unknown>)[call.method]
    if (typeof method === "function") {
      ;(method as (...args: unknown[]) => unknown).apply(client, call.args)
      if (call.method === "reset") minimizeFlagEvaluationProperties(client)
    }
  } catch {
    // Analytics must never block the product path.
  }
}

function attachFeatureFlagListener(
  client: PostHog,
  listener: PendingFeatureFlagListener
): void {
  if (listener.cancelled) return
  const unsubscribe = client.onFeatureFlags(listener.callback)
  if (typeof unsubscribe === "function") listener.unsubscribe = unsubscribe
}

function createDeferredPostHogClient(): DeferredPostHogClient {
  return {
    capture: (...args) => enqueuePostHogCall("capture", args),
    captureException: (...args) => enqueuePostHogCall("captureException", args),
    identify: (...args) => enqueuePostHogCall("identify", args),
    register: (...args) => enqueuePostHogCall("register", args),
    reset: (...args) => enqueuePostHogCall("reset", args),
    unregister: (...args) => enqueuePostHogCall("unregister", args),
    getFeatureFlag: (key) => {
      requestPostHogInitialization()
      return deferredPostHogTarget?.getFeatureFlag(key)
    },
    get_distinct_id: () => deferredPostHogTarget?.get_distinct_id() ?? "",
    onFeatureFlags: (callback) => {
      const listener: PendingFeatureFlagListener = {
        callback,
        cancelled: false,
      }
      pendingFeatureFlagListeners.add(listener)
      requestPostHogInitialization()

      if (deferredPostHogTarget) {
        attachFeatureFlagListener(deferredPostHogTarget, listener)
      }

      return () => {
        listener.cancelled = true
        listener.unsubscribe?.()
        pendingFeatureFlagListeners.delete(listener)
      }
    },
  }
}

function connectDeferredPostHog(client: PostHog): void {
  deferredPostHogTarget = client
  window.posthog = client

  for (const call of deferredPostHogCalls.splice(0)) {
    invokePostHogCall(client, call)
  }
  for (const listener of pendingFeatureFlagListeners) {
    attachFeatureFlagListener(client, listener)
  }
}

async function initializePostHog(): Promise<void> {
  if (!POSTHOG_KEY || deferredPostHogTarget) return

  if (postHogInitializationPromise) {
    await postHogInitializationPromise
    return
  }

  postHogInitializationPromise = initializePostHogRuntime()
  try {
    await postHogInitializationPromise
  } catch (error) {
    postHogInitializationPromise = null
    throw error
  }
}

async function initializePostHogRuntime(): Promise<void> {
  if (!isPostHogTelemetryEnabled() || !POSTHOG_KEY || deferredPostHogTarget) return

  const { default: posthog } = await import("posthog-js")

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_API_HOST,
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    persistence: "localStorage+cookie",
    secure_cookie: true,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    capture_performance: true,
    capture_exceptions: CAPTURE_EXCEPTIONS
      ? {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        }
      : false,
    session_recording: sessionRecordingConfig,
    // Loading replay is intentionally separated from analytics initialization;
    // rrweb snapshotting is one of the largest main-thread tasks in the trace.
    disable_session_recording: true,
    enable_recording_console_log: false,
    // In the pinned SDK remote log enablement overrides the local boolean.
    // Logs have a separate transport (including URL context), outside before_send.
    logs: { captureConsoleLogs: false, beforeSend: () => null },
    person_profiles: "identified_only",
    request_batching: true,
    cross_subdomain_cookie: false,
    debug: false,
    disable_web_experiments: false,
    before_send: beforeSend,
    loaded: handlePostHogLoaded,
  })

  connectDeferredPostHog(posthog)
}

function requestPostHogInitialization(): void {
  void initializePostHog().catch(() => undefined)
}

function schedulePostHogInitialization(): void {
  // No fixed delay after load: visitors who leave before PostHog initializes
  // never send a $pageview, which hid most short visits from web analytics.
  const scheduleAfterLoad = () => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(requestPostHogInitialization, { timeout: 2_000 })
      return
    }
    requestPostHogInitialization()
  }

  if (document.readyState === "complete") {
    scheduleAfterLoad()
    return
  }
  window.addEventListener("load", scheduleAfterLoad, { once: true })
}

function unregisterMarketingAttribution(client: AttributionPostHogClient): void {
  for (const key of [
    ...MARKETING_ATTRIBUTION_KEYS,
    // Cleanup only: remove the superseded PR's classifier from preview browsers.
    "acq_source", "acq_channel", "acq_placement", "acq_campaign_id", "acq_campaign",
    "acq_content_id", "acq_offer_id", "acq_lead_magnet_id", "acq_taxonomy_version",
    "acq_capture_method",
  ]) {
    client.unregister(key)
  }
}

if (
  isPostHogTelemetryEnabled() &&
  POSTHOG_KEY &&
  typeof window !== "undefined" &&
  !window.__PH_INITED
) {
  window.__PH_INITED = true
  const deferredClient = createDeferredPostHogClient()
  window.posthog = deferredClient
  applyMarketingAttribution(deferredClient)
  schedulePostHogInitialization()
}
